import { useState } from 'react'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  alpha,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material'
import { computeRollupPreview } from '@/utils/rollup-live-example'
import { RollupKeyField } from './useRollupKeyFields'

type RollupLiveExampleProps = {
  keyFields: RollupKeyField[]
  keptFieldIds: Set<string>
  /** Initial open/closed state. Only read once: the user's own toggle after
   * that persists for the life of this card. */
  defaultOpen: boolean
}

/** One rail color per group, cycling — purely a visual grouping cue, so
 * reusing colors once there are more groups than colors is fine. */
const RAIL_COLORS = [
  'primary',
  'success',
  'warning',
  'secondary',
  'info',
] as const satisfies ReadonlyArray<
  'primary' | 'success' | 'warning' | 'secondary' | 'info'
>

/**
 * The rollup config card's "Live example" section: a collapsible,
 * client-side before/after preview computed from a fixed illustrative
 * sample dataset, recomputed whenever the card's grouping key changes.
 */
export default function RollupLiveExample({
  keyFields,
  keptFieldIds,
  defaultOpen,
}: RollupLiveExampleProps) {
  const [open, setOpen] = useState(defaultOpen)

  const preview = computeRollupPreview(keyFields, keptFieldIds)

  return (
    <Accordion
      disableGutters
      variant="outlined"
      expanded={open}
      onChange={(_, expanded) => setOpen(expanded)}
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          px: 1.75,
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', fontWeight: 600 }}
          >
            Live example
          </Typography>
          <Typography variant="body2" color="primary.main" fontWeight={500}>
            {preview.beforeCount} lines &rarr; {preview.afterCount}{' '}
            {preview.afterCount === 1 ? 'line' : 'lines'}
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 1.75 }}>
        <Stack direction="row" sx={{ px: 0.5, pb: 1 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ flex: 1, fontSize: 11, letterSpacing: 0.6 }}
          >
            EXTRACTED LINES &middot; {preview.beforeCount}
          </Typography>
          <Box sx={{ width: 40 }} />
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ flex: 1, fontSize: 11, letterSpacing: 0.6 }}
          >
            EXPORTED TO ERP &middot;{' '}
            <Typography
              component="span"
              variant="caption"
              fontWeight={600}
              color="primary.main"
            >
              {preview.afterCount}
            </Typography>
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          {preview.groups.map((group, groupIndex) => {
            const railPaletteKey = RAIL_COLORS[groupIndex % RAIL_COLORS.length]
            const rail = `${railPaletteKey}.main` as const
            return (
              <Stack
                key={groupIndex}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  borderRadius: 1,
                  p: 0.75,
                  bgcolor: group.isMerged
                    ? (theme) => alpha(theme.palette[railPaletteKey].main, 0.07)
                    : 'transparent',
                }}
              >
                <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                  {group.lines.map((line, lineIndex) => (
                    <Stack
                      key={lineIndex}
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        borderLeft: 3,
                        borderColor: rail,
                      }}
                    >
                      <Typography variant="caption">
                        {line.keptText}
                        {line.restText && (
                          <Typography
                            component="span"
                            variant="inherit"
                            color="text.disabled"
                          >
                            {' · '}
                            {line.restText}
                          </Typography>
                        )}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{ flex: 'none' }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {line.qtyRateText}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {line.amountText}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
                <Box
                  sx={{
                    color: group.isMerged ? rail : 'text.disabled',
                    fontSize: 18,
                    flex: 'none',
                  }}
                >
                  &#10230;
                </Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderLeftWidth: 3,
                    borderLeftColor: rail,
                    bgcolor: group.isMerged
                      ? 'action.selected'
                      : 'action.hover',
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {group.mergedKeptText}
                    {group.mergedRestText && (
                      <Typography
                        component="span"
                        variant="inherit"
                        fontWeight={400}
                        color="text.disabled"
                      >
                        {' · '}
                        {group.mergedRestText}
                      </Typography>
                    )}
                  </Typography>
                  <Stack direction="row" spacing={1.5} sx={{ flex: 'none' }}>
                    {group.mergedQtyRateText && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {group.mergedQtyRateText}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {group.mergedAmountText}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            )
          })}
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          Dimmed values aren&apos;t grouping keys — they still export, taking
          the value from the line with the largest amount within the group.
        </Typography>
      </AccordionDetails>
    </Accordion>
  )
}
